import React, {useEffect, useMemo, useState, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import BTable from 'react-bootstrap/Table';
import { faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColumnDef, ColumnFiltersState, flexRender, getCoreRowModel, PaginationState, SortingState, useReactTable } from '@tanstack/react-table';

import { FilterSelector } from "./datatable.server.filterselector";
import { Pagination } from "./datatable.server.pagination";

interface DataTableProps<T extends object> {
    id?: number,
    tableColumns: ColumnDef<T>[];
    tableData: T[],
    hasNextPage: boolean,
    showPaging: boolean,
    queryFunction: (pagination: PaginationState, columnFilters: ColumnFiltersState, sorting: SortingState) => Promise<void>,
}

function ServerSideDataTable<T extends object>({id = Math.random(), tableColumns, tableData, hasNextPage, queryFunction}: DataTableProps<T>) {
    const columns = useMemo(() => tableColumns, [tableColumns]);
    const data = useMemo(() => tableData, [tableData]);
    const [loading, setLoading] = useState(false);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });
    const [refreshToken, setRefreshToken] = useState(0);

    const fetchData = useCallback((pageIndex: number) => {
        setLoading(true);
        queryFunction({pageIndex: pageIndex, pageSize: pagination.pageSize}, columnFilters, sorting)
            .finally(() => setLoading(false));
    }, [columnFilters, queryFunction, sorting, pagination.pageSize]);

    useEffect(() => {
        if (pagination.pageIndex > 0) {
            setPagination(p => ({...p, pageIndex: 0}));
        } else {
            setRefreshToken(r => r + 1);
        }
    }, [fetchData]);

    useEffect(() => {
        setRefreshToken(r => r + 1);
    }, [pagination.pageIndex]);

    useEffect(() => {
        fetchData(pagination.pageIndex);
    }, [refreshToken]);

    const table = useReactTable(
        {
            columns,
            data,
            manualPagination: true,
            manualFiltering: true,
            manualSorting: true,
            state: {
                columnFilters,
                pagination,
                sorting,
            },
            onColumnFiltersChange: setColumnFilters,
            onPaginationChange: setPagination,
            onSortingChange: setSorting,
            getCoreRowModel: getCoreRowModel(),
            enableGlobalFilter: true,
        },
    );

    if (loading) {
        return (
            <div className="d-flex justify-content-center mb-3">
                <Spinner animation="border" />
            </div>
        );
    }

    return (
        <>
            <BTable key={id} responsive striped bordered hover size="sm">
                <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header =>
                            (<th key={header.id} colSpan={header.colSpan} className="align-top">
                                {header.isPlaceholder
                                    ? null
                                    : <>
                                        <div style={header.column.getCanSort()
                                            ? {cursor: "pointer"}
                                            : {}}
                                            onClick={header.column.getToggleSortingHandler()}>
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            {header.column.getCanSort()
                                                ? <span className="float-end">
                                                    {header.column.getNextSortingOrder() === "asc"
                                                        ? <FontAwesomeIcon icon={faSortUp} />
                                                        : header.column.getNextSortingOrder() === "desc"
                                                            ? <FontAwesomeIcon icon={faSortDown} />
                                                            : null}
                                                </span>
                                                : null
                                            }
                                        </div>
                                        {header.column.getCanFilter()
                                            ? <div className="align-self-end"><FilterSelector column={header.column} /></div>
                                            : null}
                                    </>}
                            </th>))}
                        </tr>))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map((row) => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map(cell => (
                                    <td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>))}
                            </tr>))}
                </tbody>
            </BTable>

            <Pagination pagination={pagination}
                setPagination={setPagination}
                nextPage={table.nextPage}
                hasNextPage={hasNextPage}
                previousPage={table.previousPage}
                hasPreviousPage={pagination.pageIndex >  0} />
        </>
    );
}

export default ServerSideDataTable;